import React from 'react';

/**
 * Combines a forwarded ref with a component's own internal ref.
 *
 * Needed wherever a design-system component already holds a ref to the element it
 * also has to expose. Two cases exist today and neither is optional:
 *
 * - `Checkbox` writes `indeterminate` onto the node, because it is a DOM property
 *   with no matching HTML attribute and React cannot set it from JSX.
 * - `Dropdown` measures its trigger with `getBoundingClientRect()` to decide
 *   whether the menu opens upward.
 *
 * Without this, adding `forwardRef` to either would have meant choosing between
 * the component's own behaviour and the caller's ref.
 */
export function useMergedRef<T>(...refs: Array<React.Ref<T> | undefined>) {
  return React.useCallback(
    (node: T | null) => {
      refs.forEach((r) => {
        if (typeof r === 'function') r(node);
        else if (r) (r as React.MutableRefObject<T | null>).current = node;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    refs,
  );
}
